import React, { useState } from 'react';

import { Box, Button, Divider, MenuItem, Paper, Stack, TextField, Typography } from '@mui/material';

import BundledExperiencesWizard from './BundledExperiencesWizard';
import CheckboxList from './CheckboxList';
import type { UpliftType } from './types';
import { AttachedExperience } from './usePackageOptionsState';

export type SoloRateBasis = 'per_stay' | 'per_night';

type OpenPanel = 'none' | 'upgrade' | 'solo';

const DEFAULT_SOLO_SUFFIX = 'Solo Traveller';

const INITIAL_UPGRADE = {
  assets: [] as Array<string>,
  suffix: '',
  sellIncrease: '',
  netIncrease: '',
  upliftType: 'property_net' as UpliftType,
};

const INITIAL_SOLO = {
  assets: [] as Array<string>,
  suffix: DEFAULT_SOLO_SUFFIX,
  occupancy: '1A',
  rateBasis: '' as SoloRateBasis | '',
  sellDecrease: '',
  netDecrease: '',
};

type PackageUpgradesCardProps = {
  baseRoomAssetIds: Array<string>;
  baseRoomLabel: (assetId: string) => string;
  onDuplicateAsSolo: (input: {
    assetIds: Array<string>;
    suffix: string;
    occupancy: string;
    rateBasis: SoloRateBasis;
    sellDecrease: number;
    netDecrease: number;
    audToNetRate: number;
  }) => void;
  onDuplicateAsUpgrade: (input: {
    assetIds: Array<string>;
    suffix: string;
    sellIncrease: number;
    netIncrease: number;
    audToNetRate: number;
    optionIndexes?: Array<number>;
    experiences?: Array<AttachedExperience>;
    propertySellIncrease?: number;
  }) => void;
  optionLabels: Array<string>;
  netCurrency: string;
  audToNetRate: number;
};

export default function PackageUpgradesCard({
  audToNetRate,
  baseRoomAssetIds,
  baseRoomLabel,
  netCurrency,
  onDuplicateAsSolo,
  onDuplicateAsUpgrade,
  optionLabels,
}: PackageUpgradesCardProps) {
  const [openPanel, setOpenPanel] = useState<OpenPanel>('none');
  const [experiencesDialogOpen, setExperiencesDialogOpen] = useState(false);
  const [upgrade, setUpgrade] = useState(INITIAL_UPGRADE);
  const [solo, setSolo] = useState(INITIAL_SOLO);

  const isBundledUplift = upgrade.upliftType === 'bundled_experience';

  function togglePanel(panel: Exclude<OpenPanel, 'none'>) {
    const next = openPanel === panel ? 'none' : panel;
    setOpenPanel(next);
    if (next === 'upgrade') {
      setUpgrade({ ...INITIAL_UPGRADE, assets: [...baseRoomAssetIds] });
    }
    if (next === 'solo') {
      setSolo({ ...INITIAL_SOLO, assets: [...baseRoomAssetIds] });
    }
  }

  function resetUpgradePanel() {
    setUpgrade(INITIAL_UPGRADE);
    setOpenPanel('none');
  }

  function resetSoloPanel() {
    setSolo(INITIAL_SOLO);
    setOpenPanel('none');
  }

  function handleApplyUpgrade() {
    onDuplicateAsUpgrade({
      assetIds: upgrade.assets,
      suffix: upgrade.suffix.trim(),
      sellIncrease: Number(upgrade.sellIncrease) || 0,
      netIncrease: Number(upgrade.netIncrease) || 0,
      audToNetRate,
    });
    resetUpgradePanel();
  }

  function handleApplySolo() {
    if (!solo.rateBasis) {
      return;
    }
    onDuplicateAsSolo({
      assetIds: solo.assets,
      suffix: solo.suffix.trim() || DEFAULT_SOLO_SUFFIX,
      occupancy: solo.occupancy || '1A',
      rateBasis: solo.rateBasis,
      sellDecrease: Number(solo.sellDecrease) || 0,
      netDecrease: Number(solo.netDecrease) || 0,
      audToNetRate,
    });
    resetSoloPanel();
  }

  return (
    <Paper sx={{ mt: 4, p: 2 }} variant="outlined">
      <Stack alignItems="flex-start" direction="row" justifyContent="space-between" spacing={1} useFlexGap>
        <Box minWidth={0}>
          <Typography fontWeight={700} variant="h6">
            Package upgrades
          </Typography>
          <Typography color="text.secondary" variant="body2">
            Duplicate selected base room types across all options to create upgrade or solo packages.
          </Typography>
        </Box>
        <Stack direction="row" flexShrink={0} spacing={1} useFlexGap>
          <Button
            disabled={baseRoomAssetIds.length === 0}
            onClick={() => togglePanel('upgrade')}
            sx={{ whiteSpace: 'nowrap' }}
            variant="outlined"
          >
            {openPanel === 'upgrade' ? '× Cancel' : '+ Duplicate as upgrades'}
          </Button>
          <Button
            disabled={baseRoomAssetIds.length === 0}
            onClick={() => togglePanel('solo')}
            sx={{ whiteSpace: 'nowrap' }}
            variant="outlined"
          >
            {openPanel === 'solo' ? '× Cancel' : '+ Duplicate as solo traveller'}
          </Button>
        </Stack>
      </Stack>

      {openPanel === 'upgrade' && (
        <Box>
          <Divider sx={{ my: 2 }} />

          <Box display="grid" gap={2} gridTemplateColumns={{ xs: '1fr', md: '2fr 1fr' }}>
            <TextField
              label="Package suffix"
              onChange={(e) => setUpgrade({ ...upgrade, suffix: e.target.value })}
              placeholder="e.g. Airport Transfers, Indulgence Package"
              size="small"
              value={upgrade.suffix}
            />
            <TextField
              label="Uplift type"
              onChange={(e) => setUpgrade({ ...upgrade, upliftType: e.target.value as UpliftType })}
              select
              size="small"
              value={upgrade.upliftType}
            >
              <MenuItem value="property_net">Increase property net rate</MenuItem>
              <MenuItem value="bundled_experience">Bundled experience</MenuItem>
            </TextField>
            {isBundledUplift ? null : (
              <Box
                display="grid"
                gap={2}
                gridColumn={{ md: '1 / -1' }}
                gridTemplateColumns={{ xs: '1fr', md: '1fr 1fr' }}
              >
                <TextField
                  inputProps={{ min: 0 }}
                  label="Sell price increase (AUD)"
                  onChange={(e) => setUpgrade({ ...upgrade, sellIncrease: e.target.value })}
                  placeholder="e.g. 200"
                  size="small"
                  type="number"
                  value={upgrade.sellIncrease}
                />
                <TextField
                  inputProps={{ min: 0 }}
                  label={`Net rate increase (${netCurrency})`}
                  onChange={(e) => setUpgrade({ ...upgrade, netIncrease: e.target.value })}
                  placeholder="e.g. 150"
                  size="small"
                  type="number"
                  value={upgrade.netIncrease}
                />
              </Box>
            )}
          </Box>

          {isBundledUplift ? (
            <Typography color="text.secondary" display="block" mt={2} variant="caption">
              Bundled experiences add ancillary sell uplift to the package price only — net rate and extra night rates
              stay unchanged (margin absorbs the uplift). Click <strong>Select experiences…</strong> to choose linked
              experiences and room types.
            </Typography>
          ) : (
            <CheckboxList
              emptyMessage="No base room types found — add rooms in Package Options first."
              items={baseRoomAssetIds}
              label={baseRoomLabel}
              onChange={(assets) => setUpgrade({ ...upgrade, assets })}
              selected={upgrade.assets}
              title="Apply to base room types"
            />
          )}

          <Stack direction="row" flexWrap="wrap" mt={2} spacing={2} useFlexGap>
            {isBundledUplift ? null : (
              <Button
                disabled={upgrade.assets.length === 0 || !upgrade.suffix.trim()}
                onClick={() => setExperiencesDialogOpen(true)}
                variant="outlined"
              >
                Add bundled experience
              </Button>
            )}
            <Button
              disabled={!upgrade.suffix.trim() || (!isBundledUplift && upgrade.assets.length === 0)}
              onClick={isBundledUplift ? () => setExperiencesDialogOpen(true) : handleApplyUpgrade}
              variant="contained"
            >
              {isBundledUplift ? 'Select experiences…' : 'Apply across all options'}
            </Button>
            <Button onClick={resetUpgradePanel} variant="text">
              Cancel
            </Button>
          </Stack>

          <Typography color="text.secondary" display="block" mt={1.5} variant="caption">
            Selected room types will be duplicated as{' '}
            <Typography component="span" fontStyle="italic" variant="inherit">
              Original Name - Suffix
            </Typography>{' '}
            across all package options. Sell prices are rounded to the nearest figure ending in 9. Use{' '}
            <strong>Add bundled experience</strong> to also attach experiences (sell rises by both uplifts; net rises by
            the property amount only).
          </Typography>
        </Box>
      )}

      {openPanel === 'solo' && (
        <Box>
          <Divider sx={{ my: 2 }} />

          <Stack direction="row" flexWrap="wrap" spacing={2} useFlexGap>
            <TextField
              label="Package suffix"
              onChange={(e) => setSolo({ ...solo, suffix: e.target.value })}
              size="small"
              sx={{ minWidth: 240 }}
              value={solo.suffix}
            />
            <TextField
              label="Occupancy"
              onChange={(e) => setSolo({ ...solo, occupancy: e.target.value })}
              size="small"
              sx={{ width: 120 }}
              value={solo.occupancy}
            />
            <TextField
              label="Rate basis"
              onChange={(e) => setSolo({ ...solo, rateBasis: e.target.value as SoloRateBasis })}
              select
              size="small"
              sx={{ minWidth: 180 }}
              value={solo.rateBasis}
            >
              <MenuItem disabled value="">
                Select…
              </MenuItem>
              <MenuItem value="per_stay">Per stay</MenuItem>
              <MenuItem value="per_night">Per night</MenuItem>
            </TextField>
            <TextField
              inputProps={{ min: 0 }}
              label="Sell price decrease (AUD)"
              onChange={(e) => setSolo({ ...solo, sellDecrease: e.target.value })}
              size="small"
              sx={{ minWidth: 200 }}
              type="number"
              value={solo.sellDecrease}
            />
            <TextField
              inputProps={{ min: 0 }}
              label={`Net rate decrease (${netCurrency})`}
              onChange={(e) => setSolo({ ...solo, netDecrease: e.target.value })}
              size="small"
              sx={{ minWidth: 200 }}
              type="number"
              value={solo.netDecrease}
            />
          </Stack>

          <CheckboxList
            emptyMessage="No base room types found — add rooms in Package Options first."
            footnote="Also creates solo versions of any upgrade packages under the selected base room types."
            items={baseRoomAssetIds}
            label={baseRoomLabel}
            onChange={(assets) => setSolo({ ...solo, assets })}
            selected={solo.assets}
            title="Apply to base room types"
          />

          <Stack direction="row" mt={2} spacing={2}>
            <Button
              disabled={solo.assets.length === 0 || !solo.rateBasis}
              onClick={handleApplySolo}
              variant="contained"
            >
              Apply across all options
            </Button>
            <Button onClick={resetSoloPanel} variant="text">
              Cancel
            </Button>
          </Stack>

          <Typography color="text.secondary" display="block" mt={1.5} variant="caption">
            Selected rooms will be duplicated as{' '}
            <Typography component="span" fontStyle="italic" variant="inherit">
              Original Name - Suffix
            </Typography>{' '}
            with solo occupancy and adjusted pricing across all package options.
          </Typography>
        </Box>
      )}

      {experiencesDialogOpen && (
        <BundledExperiencesWizard
          baseRoomAssetIds={baseRoomAssetIds}
          baseRoomLabel={baseRoomLabel}
          initialAssets={upgrade.assets}
          initialPropertyNet={upgrade.netIncrease}
          initialPropertySell={upgrade.sellIncrease}
          initialSuffix={upgrade.suffix}
          netCurrency={netCurrency}
          onApply={({
            assetIds,
            experiences,
            netIncrease,
            optionIndexes,
            propertySellIncrease,
            sellIncrease,
            suffix,
          }) => {
            onDuplicateAsUpgrade({
              assetIds,
              suffix,
              sellIncrease,
              netIncrease,
              audToNetRate,
              optionIndexes,
              experiences,
              propertySellIncrease,
            });
            resetUpgradePanel();
          }}
          onClose={() => setExperiencesDialogOpen(false)}
          open={experiencesDialogOpen}
          optionLabels={optionLabels}
        />
      )}
    </Paper>
  );
}
